import { useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { usePortalStore } from '../../stores';
import { portalBrandingApi, portalAuthApi } from '../../services/api';
import { SideNav, TopBar } from '../instrument/Shell';
import FirstRunOnboarding from '../instrument/FirstRunOnboarding';
import {
  LayoutDashboard, FileText, MessageSquare, Wrench, Key, BarChart3,
  Receipt, Settings, Coins, TrendingUp, Users, Cuboid, Sparkles,
} from 'lucide-react';
import { CreditMeter } from '../billing/CreditMeter';
import { SuperAdminPill } from '../instrument/SuperAdminPill';
import { UploadProgressDock } from '../knowledge/UploadProgressDock';

const allNavItems = [
  { to: '/portal/dashboard', label: 'Dashboard',    icon: LayoutDashboard, key: 'dashboard' },
  { to: '/portal/chat',      label: 'Agent Chat',   icon: MessageSquare,   key: 'chat' },
  // Knowledge: admins get full upload/edit; org members get a read-only
  // view (we keep it visible so they can browse what their agent can
  // answer from). Personal accounts (single-seat) always get it.
  { to: '/portal/knowledge', label: 'Knowledge',    icon: FileText,        key: 'knowledge' },
  { to: '/portal/design-studio', label: 'Design Studio', icon: Sparkles, key: 'design-studio' },
  { to: '/portal/assets',    label: '3D Assets',    icon: Cuboid,          key: 'assets' },
  { to: '/portal/tools',     label: 'Tools',        icon: Wrench,          key: 'tools' },
  // Team: org admins only (personal/single-seat tenants don't have a
  // team; org members can't see other members' data).
  { to: '/portal/team',      label: 'Team',         icon: Users,           key: 'team', orgOnly: true, adminOnly: true },
  { to: '/portal/analytics', label: 'Analytics',    icon: BarChart3,       key: 'analytics' },
  { to: '/portal/api-keys',  label: 'API Keys',     icon: Key,             key: 'api-keys' },
  { to: '/portal/usage',     label: 'Usage',        icon: TrendingUp,      key: 'usage' },
  { to: '/portal/orders',    label: 'Orders',       icon: Receipt,         key: 'orders' },
  { to: '/portal/pricing',   label: 'Buy Credits',  icon: Coins,           key: 'pricing', adminOnly: true },
  { to: '/portal/settings',  label: 'Settings',     icon: Settings,        key: 'settings' },
];

const navFor = (user, tenant) => {
  const isPersonal = (tenant?.account_type === 'personal');
  const isAdmin = (user?.role === 'admin') || !!user?.is_super_admin;
  const isDemo = !!user?.is_demo;
  return allNavItems.filter((n) => {
    // Demo users can't purchase or manage orders (Feb 2026 trial policy).
    if (isDemo && (n.key === 'orders' || n.key === 'pricing')) return false;
    if (n.orgOnly && isPersonal) return false;
    // Personal accounts are effectively their own admin so they keep
    // adminOnly entries (Team is filtered out separately by orgOnly).
    if (n.adminOnly && !isAdmin && !isPersonal) return false;
    return true;
  });
};

const pageTitleFor = (path) => {
  const hit = allNavItems.find((n) => path === n.to || path.startsWith(n.to + '/'));
  return hit ? hit.label : 'Portal';
};

const PortalLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { token, user, tenant, logout: clearAuth, setIdentity } = usePortalStore();
  // The chat route needs the full main area (no outer padding) so its
  // own sticky top bar can pin correctly. Every other portal page
  // keeps the standard centered padded container.
  const isChat = /^\/portal\/chat(\/|$)/.test(location.pathname);

  // Refresh tenant + user identity on mount so the sidebar/breadcrumb
  // immediately reflect any admin-side rename (cached login data goes stale).
  useEffect(() => {
    if (!token) return;
    portalAuthApi.getMe()
      .then((res) => setIdentity(res.data.user, res.data.tenant))
      .catch(() => {});
  }, [token, setIdentity]);

  useEffect(() => {
    if (!token) return;
    portalBrandingApi.get()
      .then((res) => {
        const b = res.data;
        const root = document.documentElement;
        if (b.primary_color) root.style.setProperty('--nexus-accent-mint', b.primary_color);
        if (b.accent_color) root.style.setProperty('--nexus-accent-violet', b.accent_color);
      })
      .catch(() => {});
    return () => {
      const root = document.documentElement;
      root.style.removeProperty('--nexus-accent-mint');
      root.style.removeProperty('--nexus-accent-violet');
    };
  }, [token]);

  if (!token) {
    navigate('/portal/login', { replace: true });
    return null;
  }

  const handleLogout = () => {
    clearAuth();
    navigate('/portal/login');
  };

  const page = pageTitleFor(location.pathname);
  const navItems = navFor(user, tenant);

  return (
    <div className="flex h-screen overflow-hidden" style={{ background: 'var(--layer-0)' }} data-testid="portal-layout">
      <SideNav
        scope="portal"
        items={navItems}
        userName={user?.name || user?.email}
        userEmail={user?.email}
        isSuperAdmin={!!user?.is_super_admin}
        onLogout={handleLogout}
      />
      <div className="flex-1 flex flex-col min-w-0">
        <TopBar
          crumbs={[page]}
          rightSlot={
            <div className="flex items-center gap-2">
              {user?.is_super_admin && <SuperAdminPill />}
              <CreditMeter />
            </div>
          }
        />
        <main className="flex-1 overflow-y-auto" style={{ background: 'var(--layer-0)' }}>
          {/* Chat takes the full main area (no padding) so its own
              sticky top bar pins correctly. Every other portal page
              keeps the standard padded container. */}
          {isChat ? (
            <div className="h-full">
              <Outlet />
            </div>
          ) : (
            <div className="px-8 py-8 lg:px-12 lg:py-10 max-w-[1600px] mx-auto">
              <Outlet />
            </div>
          )}
        </main>
      </div>
      <FirstRunOnboarding userName={user?.name} scope="portal" />
      <UploadProgressDock />
    </div>
  );
};

export default PortalLayout;
